<template>
  <div
    :class="
      $b({
        customMode: customiserMode,
        'prescription-product': customisedProduct.isPrescription,
      })
    "
  >
    <ReviewSidebar
      :visible="reviewSidebarOpen"
      :customised-product="customisedProduct"
      :is-composite-pdp="isComposite"
      :product-ids="productIds"
      :skus-for-reviews="skusForReviews"
      :title-override="reviewsTitleOverride"
      :review-summary="reviewSummary"
      v-bind="$attrs"
      @close-sidebar="closeReviews"
    />
    <pd-microdata
      :name="customisedProduct.name"
      :sku="customisedProduct.legacySKU"
      :description="customisedProduct.metaDescription"
      :base-sku="customisedProduct.baseSKU"
      :price="customisedProduct.price"
      :customised-product="customisedProduct"
      :review-summary="reviewSummary"
      :currency="customisedProduct.product.currency"
      :in-stock="customisedProduct.isInStock"
    />

    <content-loop
      v-if="pdpConfig.rowsAbove.length"
      :class="$b('rows-above')"
      :rows="pdpConfig.rowsAbove"
      :active-variant-sku="customisedProduct.legacySKU"
    />

    <div
      id="top"
      :class="$b('wrapper')"
    >
      <div :class="$b('title-container')">
        <StockThresholdIndicator
          v-if="showStockThresholdIndicator"
          :customised-product="customisedProduct"
          :class="$b('stock-threshold-indicator')"
        />
        <ProductTitleSection
          :customised-product="customisedProduct"
          :product-name="productName"
          :frame-type="
            customisedProduct.frameTypes.length > 1
              ? customisedProduct.frameType ||
                customisedProduct.product.frameTypeOverride
              : null
          "
          :subtitle="productSubtitle"
          :subtitle-above-title="false"
          :show-prescription-title="selectedProductType === 'prescription'"
          :show-reviews="
            (showReviews && !useGenericReviews) || !isCasesPageMode
          "
          :review-summary="reviewSummary"
          :pre-discount-price="preDiscountPrice"
          :price="price"
          :is-minimum-price="customisedProduct.showMinimumPrice"
          @launch-reviews="launchReviews"
        />
      </div>
      <div :class="$b('sub-grid')">
        <div :class="$b('images')">
          <div :class="$b('image-gallery')">
            <ProductGallery
              :customised-product="customisedProduct"
              :show-iris-preview="showIrisPreview"
              :static-product-and-lifestyle-images="
                staticProductAndLifestyleImages
              "
              :static-view-angles="staticViewAngles"
              :show360="show360"
              :layer-template="layerTemplate"
              :active-lifestyle-index="activeLifestyleIndex"
              :show-lens-guide-button="showLensGuideButton"
              @show-lens-guide="showLensGuide = $event"
              @launch-fullscreen-product-modal="
                handleLaunchFullscreenProductModal
              "
            />
          </div>
        </div>

        <div :class="$b('info-text')">
          <div :class="$b('breadcrumbs')">
            <ProductBreadcrumbs
              v-if="!isCasesPageMode"
              :series-label="pdpConfig.relatedProducts.breadcrumbLabel"
              :series-target="pdpConfig.relatedProducts.breadcrumbTarget"
              :has-limited-edition-part="
                customisedProduct.hasLimitedEditionPart
              "
              :is-limited-edition="customisedProduct.isLimitedEdition"
              :product-name="customisedProduct.product.name"
            />
          </div>

          <ProductInfoText
            v-if="
              (!isCasesPageMode &&
                customisedProduct.product.pdpConfig.showProductInfoTextBlock) ||
              customisedProduct.product.pdpConfig.pdpText
            "
            :customised-product="customisedProduct"
          />
        </div>

        <div v-if="pdpPageContentProductAccordions?.length" :class="$b('product-info-accordion')">
          <ProductInfoAccordion
            :show360="show360 && !customisedProduct.isLimitedEdition"
            :customised-product="customisedProduct"
            :accordions="pdpPageContentProductAccordions"
            :layer-template="layerTemplate"
            :show-iris-preview="showIrisPreview"
          />
        </div>

        <div
          id="product-info"
          :class="
            $b('product-info', {
              dialogOpen: dialogOpen
            })
          "
        >
          <div :class="$b('product-info-sticky-container')">
            <ProductInfoPanel
              ref="infoPanel"
              v-model:customiser-mode="customiserMode"
              v-model:show-iris-preview="showIrisPreview"
              :customised-product="customisedProduct"
              :review-summary="reviewSummary"
              :cart-updating="cartUpdating"
              :cart-updated="cartUpdated"
              :is-composite-pdp="isComposite"
              :composite-products="compositeProducts"
              :skus-for-reviews="skusForReviews"
              :product-ids="productIds"
              :title-override="reviewsTitleOverride"
              :review-sidebar-open="reviewSidebarOpen"
              :price="price"
              :pre-discount-price="preDiscountPrice"
              :icons-follow-lens="iconsFollowLens"
              :logos-follow-lens="logosFollowLens"
              :bottom-follow-frame="bottomFollowFrame"
              :frame-follow-strap="frameFollowStrap"
              :show-lens-tech-select="showLensTechSelect"
              :prescription-mode="
                ['prescription', 'eyeglasses'].includes(selectedProductType)
              "
              :sku-on-load="skuOnLoad"
              :active-part-type="activePartType"
              :show-stock-threshold-indicator="showStockThresholdIndicator"
              :has-lifetime-guarantee="hasLifetimeGuarantee"
              :selected-product-type="selectedProductType"
              :swatch-select-variants="swatchSelectVariants"
              :composite-variants="compositeVariants"
              v-bind="$attrs"
              @change-lens-tech="handleLensTechChange"
              @choice-selected="handleChoice"
              @show-lens-guide="showLensGuide = true"
              @failed-over-lens-tech="
                customisedProduct.setLensTechSelectedState(false)
              "
              @review-clicked="handleReviewClick"
              @dialog-open-state-changed="dialogOpen = $event"
              @close-sidebar="closeReviews"
              @show-reviews="launchReviews"
              @unlink-icons-and-lenses="unlinkIconLens"
              @toggle-icon-lens-link="toggleIconLensLink"
              @toggle-bottom-frame-link="toggleBottomFrameLink"
              @unlink-bottom-and-frame="bottomFollowFrame = false"
              @toggle-frame-strap-link="toggleStrapFrameLink"
              @unlink-frame-and-strap="frameFollowStrap = false"
              @change-customiser-tab="updateActivePartType"
              @show-prescription-how-it-works-modal="
                prescriptionHowItWorksVisible = true
              "
              @prescription-mode-toggled="handlePrescriptionModeToggled"
              @toggled-product-type="selectedProductType = $event"
              @toggle-promo-selector="promoSelectorOpen = $event"
              @set-primary-sku-added-to-cart="handlePrimarySkuAddedToCart"
            />
          </div>

          <PromoSelectorModal
            v-if="hasChoiceOfGiftDiscounts && promoSelectorOpen"
            :promo-selector-open="promoSelectorOpen"
            :customised-product="customisedProduct"
            :primary-sku-added-to-cart="primarySkuAddedToCart"
            @toggle-promo-selector="handleTogglePromoSelector($event)"
          />
        </div>
      </div>
    </div>

    <div :class="$b('body')">
      <div :class="$b('content-loop-wrapper')">
        <ProductInstaShop
          :customised-product="customisedProduct"
          @change-customised-product="handleChangeCustomisedProductFromSku"
        />

        <ProductTechSpecs
          v-if="pdpConfig.techSpecs.length"
          :customised-product="customisedProduct"
        />

        <LensGuideBanner
          v-if="
            !customisedProduct.isLimitedEdition &&
            customisedProduct.lensPart &&
            customisedProduct.lensChoice
          "
          v-model:show-iris-preview="showIrisPreview"
          :customised-product="customisedProduct"
          :selected-scene="lensPreviewScene"
          @show-lens-guide="showLensGuide = true"
        />

        <ProductSpecification
          v-if="
            customisedProduct.frameSpec ||
            customisedProduct.lensSpec ||
            customisedProduct.measurements
          "
          :customised-product="customisedProduct"
        />

        <ContentLoop
          v-if="rowsBelow.length"
          :class="$b('rows-below')"
          :rows="rowsBelow"
          :active-variant-sku="customisedProduct.legacySKU"
        />

        <template v-if="pdpPageContentSections.length">
          <template
            v-for="pageSection in pdpPageContentSections"
            :key="pageSection.id"
          >
            <CardScroller :block="pageSection" />
          </template>
        </template>
      </div>

      <ProductBannerCarousel
        name="announcements"
        :customised-product="customisedProduct"
      />

      <PaceComparison
        v-if="
          ['vulcans', 'velans', 'airas', 'ultras', 'gts'].includes(
            customisedProduct.product.sku
          )
        "
        :customised-product="customisedProduct"
        :frame-type="customisedProduct.frameType"
        :review-summary="reviewSummary"
      />

      <template
        v-if="
          ['vulcans', 'velans', 'airas'].includes(customisedProduct.baseSKU) &&
          !customisedProduct.isLimitedEdition &&
          customisedProduct.product.pageMode === 'custom'
        "
      >
        <pd-convert-your-kit
          :customised-product="customisedProduct"
          :conversion-kit-enabled="customisedProduct.conversionKitEnabled"
          :frame-type="customisedProduct.frameType"
        />

        <pd-pace-features
          :base-sku="customisedProduct.baseSKU"
          :product-name="customisedProduct.name"
        />
      </template>

      <ProductTestimonialQuotes
        v-if="pdpConfig.quotes?.length"
        :quotes="pdpConfig.quotes"
        :title="pdpConfig.quotesSectionTitle"
      />

      <ClientOnly>
        <LazyLensGuideModal
          v-if="
            showLensGuide &&
            !customisedProduct.isLimitedEdition &&
            customisedProduct.lensPart
          "
          v-model:visible="showLensGuide"
          v-model:show-iris-preview="showIrisPreview"
          :initial-scenes="initialLensGuideScenes"
          :customised-product="customisedProduct"
          :show360="show360"
          :icons-follow-lens="iconsFollowLens"
          :logos-follow-lens="logosFollowLens"
          :bottom-follow-frame="bottomFollowFrame"
          :frame-follow-strap="frameFollowStrap"
          :prescription-mode="selectedProductType === 'prescription'"
          @select="handleChoice"
          @change-lens-tech="handleLensTechChange"
          @unlink-icons-and-lenses="unlinkIconLens"
          @toggle-icon-lens-link="toggleIconLensLink"
          @toggle-bottom-frame-link="toggleBottomFrameLink"
          @unlink-bottom-and-frame="bottomFollowFrame = false"
          @toggle-frame-strap-link="toggleStrapFrameLink"
          @unlink-frame-and-strap="frameFollowStrap = false"
          @prescription-mode-toggled="handlePrescriptionModeToggled"
        />
      </ClientOnly>

      <ReviewSummaryBanner
        v-if="showReviews"
        :customised-product="customisedProduct"
        :review-summary="reviewSummary"
        :title-override="reviewsTitleOverride"
        @show-reviews="launchReviews"
      />

      <pd-crosssell
        :base-sku="customisedProduct.baseSKU"
        :sku-prefix="customisedProduct.product.sku"
      />

      <FadeTransition>
        <FullScreenProductModal
          v-if="showFullScreenModal"
          v-model:active-slide-index="activeSlideIndex"
          v-model:active-lifestyle-index="activeLifestyleIndex"
          :static-product-images="staticViewAngles"
          :show360="show360 && layerTemplate.supports360 !== false"
          @close="toggleFullScreenModal"
        >
          <ProductOrLifestyleImage
            v-model:active-slide-index="activeSlideIndex"
            v-model:active-lifestyle-index="activeLifestyleIndex"
            v-model:active-frame-number="activeFrameNumber"
            :customised-product="customisedProduct"
            :show-iris-preview="showIrisPreview"
            :lifestyle-images="lifestyleImages"
            :show360="show360"
            :static-product-images="staticViewAngles"
            :static-product-and-lifestyle-images="
              staticProductAndLifestyleImages
            "
            :layer-template="layerTemplate"
          />
        </FullScreenProductModal>
      </FadeTransition>

      <PrescriptionHowToModal
        v-if="prescriptionHowItWorksVisible"
        v-model:visible="prescriptionHowItWorksVisible"
      />
    </div>
  </div>
</template>

<script>
import { storeToRefs } from 'pinia'
import { stripQueryParam } from '~/assets/js/helpers/url'
import CustomisedProduct from '~/models/CustomisedProduct'
import allLayerTemplates from '~/assets/360/all.mjs'
import getCompositePDPQuery from '@/gql/product/compositePdp.gql.js'
import { getScene } from '@/gql/product/lensGuideScene.gql.js'
import { productReviewAggregationsQuery } from '~/gql/reviews.gql'
import { setPageTheme } from '~/assets/js/helpers/pageThemes'
import { getPDPPageContent } from '~/gql/product/products.gql'

export default defineNuxtComponent({
  name: 'ProductDetailPage',

  key: '_pdp',

  async setup() {
    const route = useRouter().currentRoute.value
    const { $product, $head, $dimg, runWithContext } = useNuxtApp()

    const { resolveLocalePath } = useUrlBuilder()

    const reviewStore = useReviewsStore()
    const { aggregated: genericReviewSummary } = storeToRefs(reviewStore)

    const { isMdBreakpointOrAbove } = useBreakpoint()

    const pageStore = usePageStore()

    const shopifyStore = useShopifyStore()

    const meta = Array.isArray(route.meta) ? route.meta : [route.meta]

    const isComposite = meta[0].isComposite ?? false
    const isCompositeRoot = meta[0].isCompositeRoot ?? false
    let compositeResponse = {}
    if (isComposite) {
      const query = getCompositePDPQuery(meta[0].slug)
      const datoResponse = await useDatoQuery({ query })
      compositeResponse = datoResponse.data.value.compositePdp
    }

    const productSKU = meta[0].sku || route.params.product

    const product = await $product.get(productSKU, true, route.query.preview)

    const { data: pdpPageContentResponse } = await useDatoQuery({
      query: getPDPPageContent,
      variables: {
        productId: [ product.id, product?.baseProduct?.id ].filter(Boolean),
      },
      prefetchedRequest: productSKU + '-pdp-content',
    })

    if (!product) {
      throw createError({
        statusCode: 404,
        statusMessage: `Product ${productSKU} not found`,
        fatal: true,
      })
    }

    await setPageTheme(
      pageStore,
      product.pdpConfigWithDefaults?.theme || 'sungod'
    )

    const customisedProduct = new CustomisedProduct(product, {}, route.query)

    const selectedProductType = ref('sunglasses')

    function getMatchingPrescriptionLens(customisedProduct) {
      /**
       *
       * Find a matching prescription lens colour based on a current non-prescription
       * lens sku without lens tech.
       *
       * e.g.
       *
       * customisedProduct.lensChoice.skuWithoutLensTech = zle_brown
       *
       * Then split skuWithoutLensTech string by '_'
       *
       * Then select the second item in the split string which is 'brown' here.
       *
       * Then find the prescription option by that colour.
       *
       */
      return customisedProduct.prescriptionSunglassesLenses.find((lens) => {
        const matchingLensFromColour = lens.sku.includes(
          customisedProduct.lensChoice.skuWithoutLensTech.split('_')[1]
        )

        return matchingLensFromColour
      })
    }

    const prescriptionMode =
      (route.query?.prescription === '1' ||
        customisedProduct.isPrescriptionSunglasses) &&
      !customisedProduct.isLimitedEdition

    const eyeglassesMode =
      (route.query?.eyeglasses || customisedProduct.isEyeglasses) &&
      !customisedProduct.isLimitedEdition

    if (prescriptionMode) {
      selectedProductType.value = 'prescription'
    } else if (eyeglassesMode) {
      selectedProductType.value = 'eyeglasses'
    }

    if (route.query.rlt === '1') {
      // Only respect the lens tech if it's a valid option
      const lensTechOptions = customisedProduct.lensOptions.map(
        (option) => option.optionTech.tech
      )
      const selectedOption = customisedProduct.lensTech

      if (lensTechOptions.includes(selectedOption)) {
        customisedProduct.setLensTechSelectedState(true)
      }
    } else {
      // if lensTech query param is in the url find and set the lens tech from the lens code
      if (route.query.lensTech) {
        const matchedLensTech = customisedProduct.lensOptions.find(
          (lensOption) => {
            return lensOption.tech === route.query.lensTech
          }
        )

        if (matchedLensTech) {
          customisedProduct.setLensTechSelectedState(true)
          customisedProduct.setChoice(matchedLensTech.sku)

          if (matchedLensTech.productOptionId !== null) {
            customisedProduct.lensChoice?.setActiveProductOption(
              matchedLensTech.productOptionId
            )
          }
        }
      } else if (selectedProductType.value === 'prescription') {
        const matchingPrescriptionLens =
          getMatchingPrescriptionLens(customisedProduct)

        customisedProduct.setChoice(
          matchingPrescriptionLens?.sku ??
            customisedProduct.prescriptionSunglassesLenses[0]?.sku
        )
        customisedProduct.setLensTechSelectedState(false)
      } else if (selectedProductType.value === 'eyeglasses') {
        const firstEyeglassesLens = customisedProduct.eyeglassesLenses[0]

        if (firstEyeglassesLens) {
          customisedProduct.setChoice(firstEyeglassesLens.sku)
          customisedProduct.setLensTechSelectedState(false)
        }
      }
    }

    const skusForReviews = isComposite
      ? compositeResponse.compositeProducts.map((prod) =>
          compositeResponse.useBaseProductReviews
            ? prod.baseProduct?.baseProduct?.sku || prod.baseProduct.sku
            : prod.sku
        )
      : customisedProduct.baseSKU

    const productIds = isComposite
      ? compositeResponse.compositeProducts.map((prod) =>
          compositeResponse.useBaseProductReviews
            ? prod.baseProduct?.baseProduct?.id || prod.baseProduct.id
            : prod.baseProduct.id
        )
      : [
          customisedProduct.product.baseProduct?.id,
          customisedProduct.product.id,
        ].filter(Boolean)

    const reviewSummaryResponse = await useDatoQuery({
      query: productReviewAggregationsQuery,
      variables: {
        products: productIds,
      },
    })

    const launchCustomiser = route.query?.customiser === '1'

    const customiserMode = ref(
      ['parts', 'lenses', 'conversionkit'].includes(product.pageMode) ||
        launchCustomiser
    )

    // Setup Meta tags
    if (isCompositeRoot && compositeResponse?.meta) {
      runWithContext(() => useHead($head(compositeResponse.meta)))
    } else if (customisedProduct.product.pdpConfig.meta) {
      runWithContext(() => {
        const head = $head(customisedProduct.product.pdpConfig.meta)

        head.meta = [...head.meta].map((metaItem) => {
          if (metaItem.hid === 'og:image') {
            metaItem.content = $dimg.url({
              sku: customisedProduct?.legacySKU,
              w: 800,
            })
          }

          return metaItem
        })

        return useHead(head)
      })
    }

    return {
      productSKU,
      customiserMode,
      customisedProduct,
      isComposite,
      isCompositeRoot,
      skusForReviews,
      compositeProducts: compositeResponse?.compositeProducts || [],
      rowsBelowOverride: compositeResponse?.rowsBelowOverride || [],
      reviewsTitleOverride: compositeResponse?.reviewsTitleOverride,
      useBaseProductReviews: compositeResponse?.useBaseProductReviews,
      skuOnLoad: route.query.sku,
      genericReviewSummary,
      isMdBreakpointOrAbove,
      pageStore,
      resolveLocalePath,
      reviewSummaryResponse,
      productIds,
      selectedProductType,
      shopifyStore,
      getMatchingPrescriptionLens,
      prescriptionMode,
      pdpPageContentResponse,
    }
  },

  data: () => ({
    showLensGuide: false,
    showIrisPreview: false,
    current360FrameNumber: 1,
    activeSlideIndex: 1,
    activeLifestyleIndex: 0,
    activeFrameNumber: 0,
    showLensTechWarning: false,
    cartUpdating: false,
    cartUpdated: false,
    showFullScreenModal: false,
    initialLensGuideScenes: {},
    dialogOpen: false,
    reviewSidebarOpen: false,
    iconsFollowLens: false,
    logosFollowLens: false,
    bottomFollowFrame: false,
    frameFollowStrap: false,
    activePartType: null,
    prescriptionHowItWorksVisible: false,
    promoSelectorOpen: false,
    primarySkuAddedToCart: ''
  }),

  computed: {
    pdpPageContentProductAccordions() {
      return this.pdpPageContentResponse?.allPdpPageContents?.flatMap(
        (content) => {
          return content.productAccordions
        }
      )
    },
    pdpPageContentSections() {
      return this.pdpPageContentResponse?.allPdpPageContents?.flatMap(
        (content) => {
          return content.sections
        }
      )
    },
    reviewSummary() {
      if (this.useGenericReviews) return this.genericReviewSummary

      const aggregations = this.reviewSummaryResponse.data.value.aggregations
      const { count, averageStars, recommendRate } = aggregations.reduce(
        (accumulator, { count, averageStars, recommendRate }) => {
          accumulator.count += count
          accumulator.averageStars += count * averageStars
          accumulator.recommendRate += count * recommendRate
          return accumulator
        },
        { count: 0, averageStars: 0, recommendRate: 0 }
      )

      return {
        recommendRate: recommendRate / count,
        count,
        averageStars: averageStars / count,
      }
    },
    pdpConfig() {
      return (
        this.customisedProduct?.product?.pdpConfigWithDefaults || {
          rowsAbove: [],
          rowsBelow: [],
        }
      )
    },

    lifestyleImages() {
      if (
        this.customisedProduct.isLimitedEdition &&
        this.customisedProduct.product.pdpConfig?.lifestyleImagesOverride
      ) {
        return this.customisedProduct.product.pdpConfig.lifestyleImagesOverride.map(
          (image) => ({
            ...image,
          })
        )
      }

      // If any part has lifestyle image overrides then return them - it's unlikely we'll have multiple overrides at once
      const choiceWithImageOverrides = Object.values(
        this.customisedProduct.choices
      ).find((choice) => choice.lifestyleImagesOverride?.length)
      if (choiceWithImageOverrides)
        return choiceWithImageOverrides.lifestyleImagesOverride.map(
          (image) => ({
            ...image,
          })
        )

      return this.customisedProduct.lifestyleImages
    },

    staticProductImages() {
      return (
        this.customisedProduct.product.pdpConfig?.staticProductImages || []
      ).map((img) => ({
        ...img,
        isStaticProductImage: true,
      }))
    },

    staticProductAndLifestyleImages() {
      return [
        ...this.staticProductImages,
        ...(this.customisedProduct.isEyeglasses ? [] : this.lifestyleImages),
      ]
    },

    staticViewAngles() {
      return this.pdpConfig?.designImagesOrder.slice(0, 3)
    },

    show360() {
      return this.customisedProduct.product.show360
    },

    layerTemplate() {
      return allLayerTemplates[this.customisedProduct.product.sku]
    },

    rowsBelow() {
      return this.rowsBelowOverride?.length
        ? this.rowsBelowOverride
        : this.pdpConfig.rowsBelow
    },

    lensPreviewScene() {
      return (
        this.customisedProduct?.product?.pdpConfig?.lensPreviewScenes[0]
          ?.name || 'beach'
      )
    },
    productName() {
      return this.customisedProduct.productName
    },
    productSubtitle() {
      return this.customisedProduct.subTitle
    },
    baseSKU() {
      return this.customisedProduct.baseSKU
    },
    preDiscountPrice() {
      return this.customisedProduct.showMinimumPrice
        ? this.customisedProduct.minimumCompareAtPrice
        : this.customisedProduct.compareAtPrice
    },
    price() {
      const price = this.customisedProduct.showMinimumPrice
        ? this.customisedProduct.minimumPrice
        : this.customisedProduct.price

      return price
    },
    useGenericReviews() {
      return this.customisedProduct.product.useGenericReviews
    },
    isCasesPageMode() {
      return this.customisedProduct.product.pageMode === 'cases'
    },
    isSimplePageMode() {
      return this.customisedProduct.product.pageMode === 'simple'
    },
    showReviews() {
      return this.customisedProduct.product.showReviews
    },
    showLensTechSelect() {
      if (this.customisedProduct.isLimitedEdition) {
        const baseProduct = this.customisedProduct.product?.baseProduct
        const allowLensTechFiltering = baseProduct?.allowLensTechFiltering
        const hideLensTech = this.customisedProduct.product?.hideLensTech
        return allowLensTechFiltering && !hideLensTech
      }

      if (
        this.customisedProduct.product.pageMode === 'parts' &&
        !this.customisedProduct.lensName
      )
        return false

      return this.customisedProduct.allowLensTechFiltering
    },
    lensGuideAvailable() {
      if (this.customisedProduct.isLimitedEdition) return false
      if (
        this.customisedProduct.product.pageMode === 'parts' &&
        !this.customisedProduct.lensName
      )
        return false
      if (this.customisedProduct.product.pageMode === 'cases') return false
      return true
    },
    showStockThresholdIndicator() {
      return (
        this.customisedProduct.product.pdpConfigWithDefaults
          .stockIndicatorThreshold >
        (this.customisedProduct.product.quantity < 0
          ? 0
          : this.customisedProduct.product.quantity)
      )
    },
    hasLifetimeGuarantee() {
      return (
        !this.isCasesPageMode &&
        !this.isSimplePageMode &&
        this.customisedProduct.product.pageMode !== 'lenses' &&
        !this.customisedProduct.baseSKU.includes('cap-')
      )
    },
    showLensGuideButton() {
      return (
        !this.customisedProduct.isLimitedEdition &&
        this.selectedProductType !== 'prescription' &&
        this.selectedProductType !== 'eyeglasses' &&
        !this.isCasesPageMode &&
        !this.isSimplePageMode &&
        this.lensGuideAvailable
      )
    },

    hasChoiceOfGiftDiscounts() {
      const discountsForProduct = this.shopifyStore.getDiscountsFromProduct(
        this.customisedProduct
      )

      if (
        this.customisedProduct?.product?.promotionProductGroup?.giftedProduct
          ?.length
      ) {
        if (
          !discountsForProduct?.anyDiscountIsFree &&
          discountsForProduct?.allDiscountOptions?.length === 1
        )
          return true
        else if (discountsForProduct?.allDiscountOptions?.length > 1)
          return true
      }

      return false
    },
    swatchSelectVariants() {
      let heroDesigns

      if (this.customisedProduct.isPrescriptionSunglasses) {
        heroDesigns = this.customisedProduct.prescriptionHeroDesigns
      } else if (this.customisedProduct.isEyeglasses) {
        heroDesigns = this.customisedProduct.eyeglassesHeroDesigns
      } else {
        heroDesigns = this.customisedProduct.heroDesigns
      }

      // get first 6 non limited edition products
      heroDesigns = heroDesigns
        .filter((item) => !item.isLimitedEdition)
        .slice(0, 6)

      // then push all limited edition hero designs in
      heroDesigns.push(
        ...this.customisedProduct.heroDesigns.filter(
          (item) => item.isLimitedEdition
        )
      )

      return [...heroDesigns]
    },
    compositeVariants() {
      return this.compositeProducts.map((product) => ({
        sku: product.sku,
        normalisedSKU: product.sku,
        swatchIcon:
          product.swatchIconOverride?.url ||
          product.baseProduct?.swatchIconOverride?.url,
        isInStock: product.isInStock,
        isOnPreorder: product.isOnPreorder,
        isLimitedEdition: true,
        swatches: [],
        url: `/shop/${this.$route.meta.slug}/${product.sku}`,
      }))
    },
  },

  watch: {
    'customisedProduct.sku'() {
      this.updateURL()
      this.trackProductView()
    },
    'customisedProduct.secondarySKU'() {
      this.updateURL()
      this.trackProductView()
    },
    'customisedProduct.conversionKitEnabled'() {
      this.updateURL()
      this.trackProductView()
    },
    'customisedProduct.isPrescriptionSunglasses'(newValue) {
      if (!newValue && !this.customisedProduct.isEyeglasses) {
        this.selectedProductType = 'sunglasses'
      } else if (newValue) {
        this.selectedProductType = 'prescription'
      }
    },
    'customisedProduct.isEyeglasses'(newValue) {
      if (!newValue && !this.customisedProduct.isPrescriptionSunglasses) {
        this.selectedProductType = 'sunglasses'
      } else if (newValue) {
        this.selectedProductType = 'eyeglasses'
      }
    },
    showLensGuide() {
      this.pageStore.toggleBlockBodyScroll(this.showLensGuide)

      // Track open/close events
      if (this.showLensGuide) {
        this.$track.lensGuide.open(this.customisedProduct, 'pdp')
      } else {
        this.$track.lensGuide.close(this.customisedProduct)
      }
    },
    showFullScreenModal() {
      this.pageStore.toggleBlockBodyScroll(this.showFullScreenModal)
    },
    selectedProductType(newValue) {
      if (newValue === 'sunglasses') {
        const matchingSunglassesLens =
          this.customisedProduct.sunglassesLenses.find((lens) => {
            const matchingLensFromColour = lens.sku.includes(
              this.customisedProduct.lensChoice.sku.split('RX')[1]
            )

            if (matchingLensFromColour) {
              return lens.quantity > 0
            }
          })

        this.customisedProduct.setChoice(
          matchingSunglassesLens?.sku ??
            this.customisedProduct.sunglassesLenses[0]?.sku
        )
        this.customisedProduct.setLensTechSelectedState(false)
      } else if (newValue === 'eyeglasses') {
        this.customisedProduct.setChoice(
          this.customisedProduct.eyeglassesLenses[0]?.sku
        )
        this.customisedProduct.setLensTechSelectedState(false)
      } else if (newValue === 'prescription') {
        const matchingPrescriptionLens = this.getMatchingPrescriptionLens(
          this.customisedProduct
        )

        this.customisedProduct.setChoice(
          matchingPrescriptionLens?.sku ??
            this.customisedProduct.prescriptionSunglassesLenses[0]?.sku
        )
        this.customisedProduct.setLensTechSelectedState(false)
      }
    },
  },

  beforeMount() {
    this.deserializeCustomisedProduct()
    this.pageStore.toggleAllowLoadingSpinner(false)
  },

  mounted() {
    this.trackProductView = this.throttleFunction(this.trackProductView, 250)

    this.updateURL()
    this.trackProductView()
    this.pushKlaviyoProductView()

    if (this.$route.query.scroll_to) {
      this.$scrollTo(`#${this.$route.query.scroll_to}`)
    }

    window.addEventListener('beforeunload', this.pushKlaviyoProductView)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.pushKlaviyoProductView()
      }
    })
    if (this.$route.query.lensguide) {
      this.showLensGuide = true
    }

    // Fetch upsells and other products when idle while using setTimeout as fallback
    if ('requestIdleCallback' in window) {
      requestIdleCallback(this.idleCallback, { timeout: 5000 })
    } else {
      setTimeout(this.idleCallback, 5000)
    }
  },

  beforeUnmount() {
    this.pushKlaviyoProductView()
    this.pageStore.toggleAllowLoadingSpinner(true)
  },

  methods: {
    getPartSwatch(part) {
      const choices = this.customisedProduct.choices
      const choiceItems = Object.values(choices)
      const partObject = choiceItems.filter((item) => item.partType === part)[0]
      return {
        ...partObject?.swatch,
      }
    },
    handlePrimarySkuAddedToCart(data) {
      this.primarySkuAddedToCart = {
        baseSku: data.baseSku,
        sku: data.sku,
      }
    },
    handlePrescriptionModeToggled(value) {
      this.selectedProductType = value ? 'prescription' : 'sunglasses'
      
      if (value) {
        this.customisedProduct.selectPrebuiltVariant(null)
      } else {
        // Attempt to re-select a prebuilt variant if available
        const matchingPrebuilts = this.customisedProduct.matchingPrebuiltVariantsIgnoringLensTech
        if (matchingPrebuilts.length) this.customisedProduct.selectPrebuiltVariant(matchingPrebuilts[0])
      }
    },
    handleChangeCustomisedProductFromSku(sku) {
      this.customisedProduct.setChoicesFromSKU(sku)
    },
    deserializeCustomisedProduct() {
      if (!this.customisedProduct) return

      if (
        !this.customisedProduct.product ||
        !this.customisedProduct?.product?.baseSKU
      ) {
        try {
          this.customisedProduct = this.$product.getCustomisedProductSync(
            this.productSKU,
            this.customisedProduct,
            true
          )
        } catch {
          return new Promise((resolve) => {
            this.$product.getCustomisedProduct(
              this.productSKU,
              this.customisedProduct,
              true,
              (customisedProduct) => {
                this.customisedProduct = customisedProduct
                resolve()
              }
            )
          })
        }
      }
    },
    handleLensTechChange({ sku, productOptionId = null, lensTechCode = null }) {
      this.customisedProduct.setLensTechSelectedState(true)
      this.customisedProduct.setChoice(sku)

      if (productOptionId !== null) {
        this.customisedProduct.lensChoice?.setActiveProductOption(
          productOptionId
        )
      }

      this.updateURL({ key: 'lensTech', value: lensTechCode })
    },
    handleChoice(option) {
      this.customisedProduct.setProductCustomisedState(true)

      if (typeof option === 'string') {
        this.customisedProduct.setChoice(option)
        return
      }
      this.customisedProduct.setChoice(option.mainPart.sku)
      if (option.followerPart.sku)
        this.customisedProduct.setChoice(option.followerPart.sku)
    },
    updateActivePartType(tab) {
      const partType = Object.values(this.customisedProduct?.choices)[0]
        ?.partType
      this.activePartType = tab || partType
    },
    updateURL(dynamicParam) {
      if (this.$route.query.scrollTop) {
        stripQueryParam(this.$route, 'scrollTop')
      }

      if (this.customisedProduct.isLimitedEdition) return

      const params = { sku: this.customisedProduct.legacySKU }

      if (this.customisedProduct.isPrebuiltVariant) {
        params.pbsku = this.customisedProduct.prebuiltSKU
      }

      if (this.customisedProduct.conversionKitEnabled)
        params.ckSKU = this.customisedProduct.secondaryLegacySKU

      if (dynamicParam) {
        params[dynamicParam.key] = dynamicParam.value
      }

      // Query params allow list
      const preserveQueryParams = [
        /preview/,
        /_vercel_no_cache/,
        /ab_.+/,
        /prescription/,
        /eyeglasses/,
        /lensTech/,
        /^hab.+/,
      ]

      preserveQueryParams.forEach((pattern) => {
        // Preserve params that match pattern
        Object.entries(this.$route.query).forEach(([key, value]) => {
          if (pattern.test(key) && value) {
            params[key] = this.$route.query[key]
          }
        })
      })

      const queryString = new URLSearchParams(params).toString()

      try {
        window.history.replaceState({}, '', `?${queryString}`)
      } catch {
      
        /* do nothing */
      }
    },
    trackProductView() {
      this.$track.product.view(this.customisedProduct, {
        visibleHeroDesignCount: this.compositeVariants?.length
          ? this.compositeVariants?.length
          : this.swatchSelectVariants?.length,
        heroDesignsOnPreorder: this.heroDesignsOnPreorder()?.length,
      })
    },
    animate360({ frameNumber, animate }) {
      this.$refs.images.animate360({ frameNumber, animate })
    },
    handleReviewClick(sku) {
      if (this.isComposite) {
        const compositeProduct = this.compositeProducts
          .sort((a, b) => b.sku.length - a.sku.length)
          .find((product) => sku.startsWith(product.sku))
        if (compositeProduct) {
          const target = `/shop/${this.$route.meta.slug}/${compositeProduct.sku}`
          window.location = this.resolveLocalePath(target)
        } else {
          const target = `/shop/redirect?sku=${sku}`
          window.location = this.resolveLocalePath(target)
        }
      } else if (this.useGenericReviews) {
        const target = `/shop/redirect?sku=${sku}`
        window.location = this.resolveLocalePath(target)
      } else {
        this.customisedProduct.setChoicesFromSKU(sku)
        this.$scrollTo('#top')
      }
    },
    toggleFullScreenModal() {
      this.showFullScreenModal = !this.showFullScreenModal
      if (!this.showFullScreenModal) this.activeSlideIndex = 1
    },
    async getInitialSceneImages() {
      if (
        this.customisedProduct.isLimitedEdition &&
        !this.customisedProduct.lensPart
      )
        return

      const previewScenes =
        this.customisedProduct?.product?.pdpConfig?.lensPreviewScenes
      if (!previewScenes.length) return

      const sceneToLoad = previewScenes[0]?.name

      const gqlQuery = getScene(sceneToLoad)
      this.query = gqlQuery

      const { data: result } = await useDatoQuery({ query: gqlQuery })
      this.initialLensGuideScenes = result?.value?.lensGuideScene
      const nakedEyeImage =
        this.initialLensGuideScenes?.nakedEyeImage?.responsiveImage
      const allPreviewScenes = this.initialLensGuideScenes?.sceneImages

      usePreloadImage(
        nakedEyeImage.srcSet,
        nakedEyeImage.webpSrcSet,
        nakedEyeImage.sizes
      )

      allPreviewScenes.forEach((sceneImage) => {
        usePreloadImage(
          sceneImage.image.responsiveImage.srcSet,
          sceneImage.image.responsiveImage.webpSrcSet,
          sceneImage.image.responsiveImage.sizes
        )
        if (sceneImage.secondaryImage) {
          usePreloadImage(
            sceneImage.secondaryImage.responsiveImage.srcSet,
            sceneImage.secondaryImage.responsiveImage.webpSrcSet,
            sceneImage.secondaryImage.responsiveImage.sizes
          )
        }
      })
    },
    pushKlaviyoProductView() {
      const _learnq = window._learnq || []
      _learnq.push([
        'track',
        'Viewed Product',
        {
          Name: this.customisedProduct.name,
          SKU: this.customisedProduct.legacySKU,
          BaseSKU: this.customisedProduct.baseSKU,
          Price: this.customisedProduct.price,
          Currency: this.storeCurrency,
          URL: this.customisedProduct.url,
          ImageURL: `https://dynamic.sungod.co/?sku=${this.customisedProduct.legacySKU}`,
        },
      ])
    },
    launchReviews() {
      this.reviewSidebarOpen = true
      this.dialogOpen = true
    },
    closeReviews() {
      this.reviewSidebarOpen = false
      this.dialogOpen = false
    },
    toggleIconLensLink() {
      this.iconsFollowLens = !this.iconsFollowLens
      this.logosFollowLens = !this.logosFollowLens
    },
    unlinkIconLens() {
      this.iconsFollowLens = false
      this.logosFollowLens = false
    },
    toggleBottomFrameLink() {
      this.bottomFollowFrame = !this.bottomFollowFrame
    },
    toggleStrapFrameLink() {
      this.frameFollowStrap = !this.frameFollowStrap
    },
    async prefetchRelatedProducts() {
      // Prefetch all products available as hero designs so the customer
      // can quickly switch between them without the page reloading
      const skusToFetch = [
        ...new Set([
          // Hero designs
          ...this.customisedProduct.heroDesigns.map((design) => {
            if (design.isLimitedEdition) return design.sku
            return design.sku.split('-')[0]
          }),

          // Composite PDP products
          ...this.compositeProducts.map((product) => product.sku),
        ]),
      ]

      return await Promise.all(
        skusToFetch.map(async (sku) => {
          return await this.$product.get(sku, true, this.$route.query.preview)
        })
      )
    },
    async idleCallback() {
      await Promise.all([
        this.prefetchRelatedProducts(),
        this.getInitialSceneImages(),
      ])
    },
    handleLaunchFullscreenProductModal(event) {
      this.activeSlideIndex = event.index + 1
      this.activeLifestyleIndex = event.index

      if (event.viewAngle) {
        this.activeFrameNumber = event.viewAngle
      }

      this.showFullScreenModal = true
    },
    async handleTogglePromoSelector(open) {
      const { $track } = useNuxtApp()

      this.promoSelectorOpen = open

      if (!open) {
        await $track.giftSelector.closed()
      }
    },
    heroDesignsOnPreorder() {
      if (this.compositeVariants?.length) {
        return this.compositeVariants.filter((compositeVariant) => {
          return compositeVariant?.isOnPreorder
        })
      } else if (this.swatchSelectVariants?.length) {
        return this.swatchSelectVariants.filter((heroDesign) => {
          return heroDesign?.isOnPreorder
        })
      }

      return []
    },
  },
})
</script>

<style lang="postcss">
.ProductDetailPage {
  @apply relative lg:bg-grey-200 pt-2;

  .ProductDetailPage__rows-above {
    @screen md {
      margin-bottom: 2em;
    }
  }

  .is-dark-chrome-theme & {
    @apply bg-off-black;
  }
}

.ProductDetailPage__breadcrumbs {
  @apply mb-8 smMax:mb-3 font-fragment-mono;
}

.ProductDetailPage__wrapper {
  @apply md:global-wrapper;

  &:before {
    @apply content-[''] fixed top-0 left-0 w-full mdMax:hidden;

    height: 100vh;
    height: 100dvh;
    background: linear-gradient(180deg, #fff 55.39%, #f7f7fa 87.56%);

    .is-dark-chrome-theme & {
      @apply content-none;
    }
  }
}

.ProductDetailPage__body {
  @apply relative;
}

.ProductDetailPage__sub-grid {
  @apply relative mdMax:overflow-clip grid lg:pb-12 md:pb-20;

  @screen md {
    @apply grid-cols-1;
    grid-column-gap: calc(2 * theme('spacing.xs'));
    grid-template-columns: 2fr minmax(360px, 1fr);
  }
  @screen lg {
    @apply gap-x-md;
  }

  &--desktop {
    @apply hidden md:block;
  }
}

.ProductDetailPage__image-gallery {
  @apply md:top-8 md:sticky;
}

.ProductDetailPage__product-info-accordion {
  @apply pb-10;

  grid-row: 3;
  grid-column: auto;
  min-width: 0;

  @media (min-width: 1201px) {
    grid-column: 1;
  }

  @screen mdMax {
    @apply bg-white z-[1];

    grid-row: 4;
  }
}

.ProductDetailPage__convincers {
  grid-row: 4;
  grid-column: auto;

  @screen md {
    grid-column: 1;
  }

  @screen mdMax {
    @apply bg-white z-[1];

    grid-row: 5;
  }
}

.ProductDetailPage__info-text {
  @apply pt-6 md:pt-10 pb-10 relative mdMax:bg-white;

  grid-row: 2;
  grid-column: 1;

  @screen mdMax {
    grid-row: 3;
  }
}

.ProductDetailPage__title-container {
  @apply mdMax:mb-6 md:hidden;
}

.ProductDetailPage__title-container,
.ProductDetailPage__product-info,
.ProductDetailPage__info-text {
  @apply global-wrapper md:global-wrapper-reset;
}

.ProductDetailPage__product-info {
  @apply pt-6 md:pt-8 relative mdMax:bg-white z-[2] min-w-0;

  grid-row: span 3;

  @screen mdMax {
    grid-row: 2;
  }

  &.ProductDetailPage__product-info--dialog-open {
    z-index: 12;
  }

  .is-dark-chrome-theme & {
    @apply bg-off-black;
  }
}

.ProductDetailPage__product-info-sticky-container {
  @apply md:h-full md:mb-5;
}

.ProductDetailPage__accordion {
  @apply relative;

  @screen mdMax {
    order: 1;
  }
}

/* Legacy PDP Styles */
.legacy-pdp {
  .side-by-side {
    position: relative;
    overflow: hidden;
  }

  .img-wrapper svg {
    opacity: 0;
  }

  .ProductDetailPage__top-links {
    position: absolute;
    top: 0;
    width: 100%;
    z-index: 6;
  }

  .right {
    position: relative;
    top: 0;
  }

  @screen mdMax {
    &.customMode,
    &.instaMode,
    &[data-page-mode='lenses'],
    &[data-page-mode='earsocks'],
    &[data-page-mode='conversionkit'],
    &[data-page-mode='parts'] {
      .ProductDetailPage__top-links {
        top: -70px;
      }

      .product-title-and-summary {
        top: 10px;
        max-height: 90px;
      }

      &[data-page-mode='parts'] .product-title-and-summary {
        top: 10px;
      }

      .right {
        top: -70px;
      }

      .img-wrapper {
        height: calc(100vh - 314px);
        padding-top: 0;
      }
    }

    &[data-page-mode='lenses'],
    &[data-page-mode='earsocks'],
    &[data-page-mode='conversionkit'] {
      .img-wrapper-inner {
        padding-bottom: 60px;
      }

      .designer-images {
        .img-wrapper {
          min-height: 500px;
        }
      }
    }
  }

  @screen sm {
    @screen mdMax {
      .img-wrapper {
        height: calc(80vh - 314px) !important;
      }
    }
  }

  &.customMode,
  &[data-page-mode='lenses']
    &[data-page-mode='earsocks']
    &[data-page-mode='conversionkit'] {
    .designer-images .img-cont {
      height: calc(100% - 80px);
    }
  }

  #img-cont-5 img.designerimg.ck-choice {
    top: 84%;
  }

  .designer-images .img-cont:not(#img-cont-5) img.ck-choice,
  .convertible-block-cont .img-cont:not(#img-cont-2) img.ck-choice {
    display: none;
  }

  .product-image-wrapper .ck-choice {
    display: none;
  }

  .ProductDetailPage__rows-above {
    @screen md {
      margin-bottom: 2em;
    }
  }

  @screen md {
    --sidebar-width: 440px;

    .ProductDetailPage__top-links {
      width: 100%;
      top: 0;
      right: 0;
    }

    .side-by-side {
      display: flex;
      justify-content: center;
      max-width: 1500px;
      margin: 0 auto;
      padding: 30px 0 50px;

      .left {
        width: calc(100% - var(--sidebar-width));
        display: inline-block;
        padding: 0 15px;
        box-sizing: border-box;

        @screen xl {
          width: calc(100% - 550px);
        }

        .img-wrapper {
          height: auto;
          min-height: calc(100vh - 295px);

          .img-wrapper-inner {
            background: none;
            top: unset;
            transform: none;

            .img-wrapper-inner-slick {
              display: flex;
              flex-wrap: wrap;

              .img-cont-wrapper {
                padding: 6px;
                width: 100%;

                @screen lg {
                  width: 50%;
                }

                display: inline-block !important;
                box-sizing: border-box;

                .img-cont {
                  @apply bg-grey-light;

                  cursor: pointer;
                  display: block !important;
                  padding-bottom: 100%;
                  height: 0;
                }
              }

              .instashop-image {
                width: 50%;
                padding: 6px;
                display: inline-block;
                box-sizing: border-box;

                .similar-image {
                  height: 0;
                  padding-bottom: 100%;
                  overflow: hidden;
                  border-radius: 10px;
                }
              }

              :not(.img-cont-wrapper) + .img-cont-wrapper,
              .img-cont-wrapper:first-child {
                width: 100%;

                .img-cont {
                  padding-bottom: 100%;
                }
              }
            }
          }
        }
      }

      .right {
        width: var(--sidebar-width);
        display: inline-block;
        padding-top: 160px;

        @screen xl {
          width: 520px;
        }

        .collapsible-body {
          padding: 0 15px;
        }
      }
    }
  }
}

.ProductDetailPage__stock-threshold-indicator {
  @apply mb-2;
}
</style>
